import type { Edge } from "@xyflow/react";
import type { SpiroNode } from "~/lib/graph";
import { getDefinition } from "~/nodes/registry";
import type { ComputeContext } from "~/nodes/types";
import type { OutputPayload } from "~/lib/types";

/** Where an input port gets its value from. */
interface Incoming {
  source: string;
  sourceHandle: string;
}

export interface EvaluationResult {
  /** Draw calls for the viewer, keyed by the id of the Output node that produced them. */
  outputs: Record<string, OutputPayload>;
  /** `previews[nodeId][controlKey]` — computed values shown read-only in node bodies. */
  previews: Record<string, Record<string, number>>;
  /** `errors[nodeId][controlKey]` — invalid expressions, rendered in red. */
  errors: Record<string, Record<string, string>>;
  /** Ids of nodes that sit on a cycle and were therefore not evaluated. */
  skipped: string[];
}

/**
 * Resolves the graph in dependency order and collects everything the UI needs.
 *
 * `@xyflow/react` only renders graphs, so this is the piece rete used to provide:
 * a Kahn topological sort over the edges, then one `compute` call per node with
 * its inputs already resolved. Nodes on a cycle are skipped rather than hung on.
 */
export function evaluate(
  nodes: SpiroNode[],
  edges: Edge[],
  time: number,
): EvaluationResult {
  const incoming = new Map<string, Map<string, Incoming>>();
  const dependents = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  const byId = new Map<string, SpiroNode>();

  for (const node of nodes) {
    byId.set(node.id, node);
    indegree.set(node.id, 0);
  }

  for (const edge of edges) {
    // Drop edges left dangling by a node deletion, and untyped/handle-less edges.
    if (!byId.has(edge.source) || !byId.has(edge.target)) continue;
    if (!edge.sourceHandle || !edge.targetHandle) continue;

    let ports = incoming.get(edge.target);
    if (!ports) incoming.set(edge.target, (ports = new Map()));

    // An input accepts a single value; the first edge wins, as in the rete version.
    if (ports.has(edge.targetHandle)) continue;
    ports.set(edge.targetHandle, {
      source: edge.source,
      sourceHandle: edge.sourceHandle,
    });

    dependents.set(edge.source, [
      ...(dependents.get(edge.source) ?? []),
      edge.target,
    ]);
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
  }

  const queue = nodes.filter((n) => indegree.get(n.id) === 0).map((n) => n.id);
  const order: string[] = [];

  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);

    for (const next of dependents.get(id) ?? []) {
      const remaining = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, remaining);
      if (remaining === 0) queue.push(next);
    }
  }

  const values = new Map<string, Record<string, unknown>>();
  const result: EvaluationResult = {
    outputs: {},
    previews: {},
    errors: {},
    skipped:
      order.length === nodes.length
        ? []
        : nodes.map((n) => n.id).filter((id) => !order.includes(id)),
  };

  for (const id of order) {
    const node = byId.get(id)!;
    const definition = getDefinition(node.type);
    if (!definition) continue;

    const ports = incoming.get(id);
    const previews: Record<string, number> = {};
    const errors: Record<string, string> = {};

    const connected = <T>(key: string): T | undefined => {
      const link = ports?.get(key);
      if (!link) return undefined;
      return values.get(link.source)?.[link.sourceHandle] as T | undefined;
    };

    const ctx: ComputeContext = {
      nodeId: id,
      data: node.data,
      time,
      connected,
      number(key, fallback = 0) {
        const upstream = connected<number>(key);
        if (typeof upstream === "number" && Number.isFinite(upstream))
          return upstream;

        const local = Number.parseFloat(String(node.data[key]));
        return Number.isFinite(local) ? local : fallback;
      },
      preview(key, value) {
        previews[key] = value;
      },
      fail(controlKey, message) {
        errors[controlKey] = message;
      },
      emit(payload) {
        result.outputs[id] = payload;
      },
    };

    try {
      values.set(id, definition.compute(ctx) ?? {});
    } catch (error) {
      values.set(id, {});
      errors["__node"] = error instanceof Error ? error.message : String(error);
    }

    if (Object.keys(previews).length > 0) result.previews[id] = previews;
    if (Object.keys(errors).length > 0) result.errors[id] = errors;
  }

  return result;
}
